/**
 * Forgot Password — multi-step OTP flow.
 *
 * Steps:
 *   1) channel + identifier (email or phone). Phone uses <PhoneInput />.
 *   2) 6-digit OTP entry. Auto-focuses, masks the recipient, supports resend.
 *   3) new password + confirm, with strength meter and visible rules.
 *   4) success screen → redirect to /login after a beat.
 *
 * Security:
 *   * We never reveal whether the identifier exists. Step-2 begins
 *     unconditionally after step-1, so step-1 never tells the user
 *     "user not found".
 *   * Resending an OTP within 60s gracefully shows the rate-limit
 *     message returned from the API.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, EnvelopeSimple, WhatsappLogo, ChatCircleDots,
  CheckCircle, LockKey, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import PhoneInput from "@/components/PhoneInput";

const CHANNELS = [
  { id: "email",    label: "Email",          icon: EnvelopeSimple },
  { id: "whatsapp", label: "WhatsApp",       icon: WhatsappLogo },
  { id: "sms",      label: "SMS",            icon: ChatCircleDots },
];

// Quick visual strength meter (same rule as backend password_is_strong).
function scorePassword(pw) {
  if (!pw) return { score: 0, label: "" };
  const classes = [
    /[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/,
  ].filter((re) => re.test(pw)).length;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (classes >= 3) score += 1;
  if (classes >= 4) score += 1;
  return {
    score,
    label: ["Too weak", "Weak", "Fair", "Good", "Strong"][score] || "",
  };
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [channel, setChannel] = useState("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");      // E.164
  const [phoneValid, setPhoneValid] = useState(false);
  const [masked, setMasked] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const otpRef = useRef(null);

  useEffect(() => {
    if (step === 2 && otpRef.current) otpRef.current.focus();
  }, [step]);

  const identifier = channel === "email" ? email.trim().toLowerCase() : phone;
  const identifierValid =
    channel === "email"
      ? /^\S+@\S+\.\S+$/.test(email.trim())
      : !!phone && phoneValid;

  const strength = scorePassword(newPassword);

  const submitStep1 = async (e) => {
    e?.preventDefault?.();
    if (!identifierValid) {
      setErr(channel === "email" ? "Enter a valid email address." : "Enter a valid phone number with country code.");
      return;
    }
    setErr(""); setInfo(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        identifier, channel,
      });
      setMasked(data.masked || identifier);
      if (data.rate_limited) {
        setInfo("We've already sent a code recently. Please wait a minute before trying again.");
      } else {
        setInfo(`If an account exists for ${data.masked || identifier}, we've sent a 6-digit code. It expires in ${data.expires_in_minutes || 10} minutes.`);
      }
      setStep(2);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const submitStep2 = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(code)) {
      setErr("Enter the 6-digit code we sent.");
      return;
    }
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", {
        identifier, channel, code,
      });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Code didn't match. Try again.");
    } finally { setBusy(false); }
  };

  const submitStep3 = async (e) => {
    e?.preventDefault?.();
    if (newPassword !== confirmPassword) {
      setErr("Passwords don't match.");
      return;
    }
    if (strength.score < 3) {
      setErr("Pick a stronger password: at least 8 characters with letters, numbers and a symbol.");
      return;
    }
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        reset_token: resetToken, new_password: newPassword,
      });
      setStep(4);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const resendCode = async () => {
    setErr(""); setInfo(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier, channel });
      setMasked(data.masked || identifier);
      setInfo(data.rate_limited
        ? "Please wait a minute before requesting another code."
        : `A fresh 6-digit code was sent to ${data.masked}.`);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col px-6 lg:px-16 py-10" data-testid="forgot-password-page">
      <Link to="/" className="flex items-center gap-2.5" data-testid="forgot-brand-link">
        <Logo size={42} tagline={true} />
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-12 w-full max-w-md mx-auto"
      >
        <StepDots step={step} />

        {step === 1 && (
          <>
            <h1 className="font-heading text-4xl font-bold leading-tight">Forgot password?</h1>
            <p className="text-ink-soft mt-2">
              No worries — pick how you'd like to receive a 6-digit reset code and we'll send it your way.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-2" data-testid="forgot-channel-row">
              {CHANNELS.map((c) => (
                <button
                  key={c.id} type="button"
                  onClick={() => { setChannel(c.id); setErr(""); }}
                  className={`p-3 rounded-2xl border text-sm font-medium transition flex flex-col items-center gap-1 ${
                    channel === c.id
                      ? "border-terracotta bg-terracotta-soft text-terracotta"
                      : "border-border bg-white hover:bg-paper-alt"
                  }`}
                  data-testid={`forgot-channel-${c.id}`}
                >
                  <c.icon size={20} weight="duotone" />
                  {c.label}
                </button>
              ))}
            </div>

            <form className="mt-6" onSubmit={submitStep1} data-testid="forgot-step1-form">
              {channel === "email" ? (
                <>
                  <label className="block text-sm font-medium mb-1.5">Email address</label>
                  <input
                    type="email" autoFocus required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@preschool.com"
                    className="w-full h-12 px-4 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta"
                    data-testid="forgot-email-input"
                  />
                </>
              ) : (
                <PhoneInput
                  label={channel === "whatsapp" ? "WhatsApp number" : "Mobile number"}
                  value={phone}
                  onChange={setPhone}
                  onValidityChange={setPhoneValid}
                  required
                  autoFocus
                  testId="forgot-phone"
                />
              )}
              {err && <div className="mt-3 text-sm text-red-600" data-testid="forgot-step1-error">{err}</div>}
              <button
                type="submit" disabled={busy || !identifierValid}
                className="mt-5 w-full h-12 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-floating"
                data-testid="forgot-step1-submit"
              >
                {busy ? "Sending code…" : (<>Send code <ArrowRight weight="bold" /></>)}
              </button>
            </form>
            <BackToLogin />
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="font-heading text-4xl font-bold leading-tight">Enter the 6-digit code</h1>
            {info && <p className="text-sm text-ink-soft mt-2" data-testid="forgot-step2-info">{info}</p>}

            <form onSubmit={submitStep2} className="mt-6" data-testid="forgot-step2-form">
              <label className="block text-sm font-medium mb-1.5">Verification code</label>
              <input
                ref={otpRef} value={code}
                inputMode="numeric" autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                className="w-full h-14 px-4 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta text-2xl tracking-[0.5em] text-center font-mono"
                data-testid="forgot-otp-input"
              />
              {err && <div className="mt-3 text-sm text-red-600" data-testid="forgot-step2-error">{err}</div>}

              <button
                type="submit" disabled={busy || code.length !== 6}
                className="mt-5 w-full h-12 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-floating"
                data-testid="forgot-step2-submit"
              >
                {busy ? "Verifying…" : (<>Verify code <ArrowRight weight="bold" /></>)}
              </button>

              <div className="mt-4 flex items-center justify-between text-sm">
                <button type="button" onClick={() => { setStep(1); setCode(""); setErr(""); }} className="text-ink-soft hover:text-ink" data-testid="forgot-back-btn">
                  Wrong details? Go back
                </button>
                <button type="button" disabled={busy} onClick={resendCode} className="text-terracotta font-semibold hover:underline inline-flex items-center gap-1" data-testid="forgot-resend-btn">
                  <ArrowCounterClockwise weight="bold" size={14} /> Resend
                </button>
              </div>
            </form>
            <BackToLogin />
          </>
        )}

        {step === 3 && (
          <>
            <h1 className="font-heading text-4xl font-bold leading-tight flex items-center gap-2">
              <LockKey weight="duotone" className="text-terracotta" /> Set a new password
            </h1>
            <p className="text-ink-soft mt-2">Pick something only you would know — minimum 8 characters with letters, numbers and a symbol.</p>

            <form onSubmit={submitStep3} className="mt-6" data-testid="forgot-step3-form">
              <label className="block text-sm font-medium mb-1.5">New password</label>
              <input
                type="password" autoFocus required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-12 px-4 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta"
                data-testid="forgot-new-pw-input"
              />
              {newPassword && (
                <div className="mt-2 flex items-center gap-2" data-testid="pw-strength">
                  {[1, 2, 3, 4].map((i) => (
                    <span key={i}
                      className={`h-1.5 flex-1 rounded-full transition ${
                        i <= strength.score
                          ? (strength.score >= 4 ? "bg-sage-deep" : strength.score === 3 ? "bg-mustard-hover" : "bg-terracotta")
                          : "bg-paper-alt"
                      }`} />
                  ))}
                  <span className="text-xs text-ink-soft min-w-[60px]">{strength.label}</span>
                </div>
              )}

              <label className="block text-sm font-medium mb-1.5 mt-4">Confirm new password</label>
              <input
                type="password" required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className={`w-full h-12 px-4 rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta ${
                  confirmPassword && newPassword !== confirmPassword ? "border-red-400" : "border-border"
                }`}
                data-testid="forgot-confirm-pw-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <div className="text-xs text-red-600 mt-1" data-testid="forgot-mismatch-error">
                  Passwords don't match.
                </div>
              )}

              {err && <div className="mt-3 text-sm text-red-600" data-testid="forgot-step3-error">{err}</div>}

              <button
                type="submit" disabled={busy || strength.score < 3 || newPassword !== confirmPassword}
                className="mt-5 w-full h-12 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-floating"
                data-testid="forgot-step3-submit"
              >
                {busy ? "Updating…" : (<>Reset password <ArrowRight weight="bold" /></>)}
              </button>
            </form>
            <BackToLogin />
          </>
        )}

        {step === 4 && (
          <div className="text-center py-10" data-testid="forgot-success">
            <CheckCircle weight="duotone" size={56} className="mx-auto text-sage-deep" />
            <h2 className="mt-4 font-heading text-3xl font-bold">Password updated</h2>
            <p className="text-ink-soft mt-2">Redirecting you to sign in…</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="forgot-step-dots">
      {[1, 2, 3].map((n) => (
        <span key={n}
          className={`h-1.5 flex-1 rounded-full transition ${
            n < step ? "bg-sage-deep" : n === step ? "bg-terracotta" : "bg-paper-alt"
          }`} />
      ))}
    </div>
  );
}

function BackToLogin() {
  return (
    <p className="text-sm text-ink-soft mt-6 text-center">
      Remembered it after all?{" "}
      <Link to="/login" className="text-terracotta font-semibold hover:underline" data-testid="forgot-back-to-login">
        Back to sign in
      </Link>
    </p>
  );
}
